import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { downscaleImage } from '@/lib/downscaleImage';
import { Upload, X } from 'lucide-react';
import { useConfirmation } from '../shared/ZCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

// ==================== UPLOAD DIALOG (Main Flow) ====================
export function UploadDialog({ open, onClose, onSave }) {
  const feedback = useConfirmation();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFiles = async (fileList) => {
    const imgs = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    setFiles(prev => [...prev, ...imgs]);
    const newPreviews = await Promise.all(imgs.map(f => new Promise(r => {
      const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(f);
    })));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      // Downscale in the browser, then upload one at a time (not Promise.all):
      // parallel large uploads split the slow link and pile onto the single
      // backend worker, which is what was dropping connections.
      for (const f of files) {
        const small = await downscaleImage(f);
        const fd = new FormData();
        fd.append('file', small);
        fd.append('is_public', isPublic);
        await api.post('/photos', fd, { timeout: 120000 });
      }
      feedback.confirm();
      setFiles([]); setPreviews([]); onSave(); onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
      setError(msg === 'Network Error' ? 'Upload failed — connection dropped. Try again.' : msg);
      feedback.fail();
    }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setFiles([]); setPreviews([]); onClose(); }}}>
      <DialogContent className="bg-white/[0.02] border-white/[0.06] text-[#f0f0f5] max-w-md" aria-describedby="upload-desc">
        <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Upload Photos</DialogTitle></DialogHeader>
        <p id="upload-desc" className="sr-only">Upload photos to Flow</p>
        <div className="space-y-4 mt-2">
          <div className="border-2 border-dashed border-white/[0.06] rounded-lg p-8 text-center cursor-pointer hover:border-[#6B35B8]/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            onDragOver={e => e.preventDefault()} data-testid="upload-dropzone">
            <Upload className="mx-auto mb-2 text-[#6b7084]" size={24} />
            <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
              Drop images here or click to select
            </p>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
              {previews.map((p, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden bg-white/[0.03]">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full text-white/60 hover:text-white">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between py-2">
            <span className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>Make public</span>
            <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid="upload-public-toggle" />
          </div>

          {error && <p className="text-xs text-[#E24B4A] text-center" data-testid="upload-error">{error}</p>}

          <Button onClick={upload} disabled={!files.length || uploading}
            className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full text-xs tracking-wider" data-testid="upload-save-btn">
            {uploading ? 'Uploading...' : `Add ${files.length || ''} photo${files.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== COLLECTION UPLOAD ====================
export function CollectionUpload({ open, onClose, collectionId, onSave }) {
  const feedback = useConfirmation();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [meta, setMeta] = useState({ tagline: '', location: '' });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFiles = async (fileList) => {
    const imgs = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    setFiles(prev => [...prev, ...imgs]);
    const newPreviews = await Promise.all(imgs.map(f => new Promise(r => {
      const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(f);
    })));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      const photoIds = [];
      for (const f of files) {
        const small = await downscaleImage(f);
        const fd = new FormData();
        fd.append('file', small);
        fd.append('is_public', 'true');
        fd.append('is_collection_only', 'true');
        if (meta.tagline) fd.append('tagline', meta.tagline);
        if (meta.location) fd.append('location', meta.location);
        const { data } = await api.post('/photos', fd, { timeout: 120000 });
        photoIds.push(data.id);
      }
      // Add to collection
      await api.post(`/framed/curated/${collectionId}/photos`, { photo_ids: photoIds });
      feedback.confirm();
      setFiles([]); setPreviews([]); setMeta({ tagline: '', location: '' });
      onSave(); onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
      setError(msg === 'Network Error' ? 'Upload failed — connection dropped. Try again.' : msg);
      feedback.fail();
    }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setFiles([]); setPreviews([]); setMeta({ tagline: '', location: '' }); onClose(); }}}>
      <DialogContent className="bg-white/[0.02] border-white/[0.06] text-[#f0f0f5] max-w-md" aria-describedby="coll-upload-desc">
        <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Upload to Collection</DialogTitle></DialogHeader>
        <p id="coll-upload-desc" className="sr-only">Upload photos to collection</p>
        <div className="space-y-4 mt-2">
          <div className="border-2 border-dashed border-white/[0.06] rounded-lg p-6 text-center cursor-pointer hover:border-[#6B35B8]/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            onDragOver={e => e.preventDefault()} data-testid="coll-upload-dropzone">
            <Upload className="mx-auto mb-2 text-[#6b7084]" size={20} />
            <p className="text-xs" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
              Drop images or click to select
            </p>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-2 max-h-24 overflow-y-auto">
              {previews.map((p, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden bg-white/[0.03]">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full text-white/60 hover:text-white">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Input value={meta.tagline} onChange={e => setMeta(p => ({ ...p, tagline: e.target.value }))} placeholder="Tagline (all)"
              className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#6b7084] text-xs" />
            <Input value={meta.location} onChange={e => setMeta(p => ({ ...p, location: e.target.value }))} placeholder="Location"
              className="bg-[#0a0a0f] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#6b7084] text-xs" />
          </div>

          {error && <p className="text-xs text-[#E24B4A] text-center" data-testid="coll-upload-error">{error}</p>}

          <Button onClick={upload} disabled={!files.length || uploading}
            className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full text-xs tracking-wider" data-testid="coll-upload-save-btn">
            {uploading ? 'Uploading...' : `Add ${files.length || ''} photo${files.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
